import React, { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, CheckCircle, Clock, Sparkles, Search } from 'lucide-react';
import { ConversationalInput } from '@/components/shared/ConversationalInput';
import { QuickPrompts } from '@/components/shared/QuickPrompts';
import { useToast } from '@/components/ui/use-toast';

interface AssessmentCriteria {
  id: string;
  title: string;
  description: string;
  prompt: string;
  placeholder: string;
  estimatedMinutes: number;
  content: string;
  completed: boolean;
  weight: number; // For scoring
}

const assessmentCriteria: AssessmentCriteria[] = [
  {
    id: 'opportunity-overview',
    title: 'Opportunity Overview',
    description: 'What is this opportunity and why are you considering it?',
    prompt: 'Describe the opportunity you\'re evaluating and what initially attracted you to it.',
    placeholder: 'Explain the role, company, or opportunity you\'re considering...',
    estimatedMinutes: 1,
    content: '',
    completed: false,
    weight: 1
  },
  {
    id: 'alignment-goals',
    title: 'Goal Alignment',
    description: 'How well does this align with your career objectives?',
    prompt: 'How does this opportunity align with your career goals and vision?',
    placeholder: 'Describe how this fits your career direction...',
    estimatedMinutes: 1,
    content: '',
    completed: false,
    weight: 3
  },
  {
    id: 'growth-potential',
    title: 'Growth Potential',
    description: 'What growth and learning opportunities does this provide?',
    prompt: 'What skills, experiences, or growth would this opportunity provide?',
    placeholder: 'List the learning and development opportunities...',
    estimatedMinutes: 1,
    content: '',
    completed: false,
    weight: 3
  },
  {
    id: 'concerns-risks',
    title: 'Concerns & Risks',
    description: 'What worries you or could go wrong?',
    prompt: 'What concerns do you have about this opportunity?',
    placeholder: 'List any concerns, risks, or red flags...',
    estimatedMinutes: 1,
    content: '',
    completed: false,
    weight: 2
  },
  {
    id: 'decision-factors',
    title: 'Decision Factors',
    description: 'What factors will drive your final decision?',
    prompt: 'What are the most important factors in making this decision?',
    placeholder: 'List the key factors that matter most...',
    estimatedMinutes: 1,
    content: '',
    completed: false,
    weight: 2
  }
];

export const ConversationalOpportunityAudit: React.FC = () => {
  const [currentCriteriaIndex, setCurrentCriteriaIndex] = useState(0);
  const [criteriaData, setCriteriaData] = useState(assessmentCriteria);
  const [startTime, setStartTime] = useState<Date | null>(null);
  const { toast } = useToast();

  useEffect(() => {
    if (!startTime) {
      setStartTime(new Date());
    }
  }, []);

  const currentCriteria = criteriaData[currentCriteriaIndex];
  const completedCriteria = criteriaData.filter(c => c.completed).length;
  const progress = (completedCriteria / criteriaData.length) * 100;
  const totalEstimatedTime = criteriaData.reduce((sum, c) => sum + c.estimatedMinutes, 0);
  const timeSpent = startTime ? Math.round((new Date().getTime() - startTime.getTime()) / 60000) : 0;

  const handleContentChange = (content: string) => {
    const updatedCriteria = [...criteriaData];
    updatedCriteria[currentCriteriaIndex] = {
      ...currentCriteria,
      content,
      completed: content.trim().length > 10
    };
    setCriteriaData(updatedCriteria);
  };

  const handleNext = () => {
    if (currentCriteria.content.trim()) {
      if (currentCriteriaIndex < criteriaData.length - 1) {
        setCurrentCriteriaIndex(currentCriteriaIndex + 1);
      } else {
        // Calculate a simple assessment score
        const totalScore = criteriaData.reduce((sum, criteria) => {
          const contentLength = criteria.content.trim().length;
          const score = Math.min(contentLength / 50, 1) * criteria.weight; // Normalize by content quality
          return sum + score;
        }, 0);
        const maxScore = criteriaData.reduce((sum, c) => sum + c.weight, 0);
        const finalScore = Math.round((totalScore / maxScore) * 100);

        toast({
          title: "Opportunity Audit Complete! 🔍",
          description: `Assessment score: ${finalScore}% - Review your analysis to make an informed decision.`,
        });
      }
    }
  };

  const handlePrevious = () => {
    if (currentCriteriaIndex > 0) {
      setCurrentCriteriaIndex(currentCriteriaIndex - 1);
    }
  };

  const handleQuickPrompt = (prompt: string) => {
    console.log('Quick prompt selected:', prompt);
  };

  const isLastCriteria = currentCriteriaIndex === criteriaData.length - 1;
  const canProceed = currentCriteria.content.trim().length > 0;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header with Progress */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-primary to-green-600 bg-clip-text text-transparent flex items-center gap-2">
              <Search className="h-6 w-6 text-primary" />
              Opportunity Audit
            </h1>
            <p className="text-muted-foreground">Evaluate opportunities with clarity and confidence</p>
          </div>
          <div className="text-right">
            <div className="text-sm text-muted-foreground">
              {completedCriteria} of {criteriaData.length} criteria evaluated
            </div>
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <Clock className="h-3 w-3" />
              <span>{timeSpent}m / ~{totalEstimatedTime}m</span>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-muted-foreground">Assessment Progress</span>
            <span className="font-medium">{Math.round(progress)}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        {/* Criteria Navigation */}
        <div className="flex gap-2 overflow-x-auto pb-2">
          {criteriaData.map((criteria, index) => (
            <Button
              key={criteria.id}
              variant={index === currentCriteriaIndex ? "default" : criteria.completed ? "secondary" : "outline"}
              size="sm"
              onClick={() => setCurrentCriteriaIndex(index)}
              className="whitespace-nowrap flex items-center gap-2"
            >
              {criteria.completed && <CheckCircle className="h-3 w-3" />}
              <span className="text-xs">{criteria.title}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* Current Criteria */}
      <div className="space-y-6">
        <Card className="p-6">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold">{currentCriteria.title}</h2>
                <p className="text-muted-foreground">{currentCriteria.description}</p>
              </div>
              <div className="flex items-center gap-2">
                <Badge variant="outline" className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  ~{currentCriteria.estimatedMinutes}m
                </Badge>
                <Badge variant="secondary">
                  Weight: {currentCriteria.weight}x
                </Badge>
              </div>
            </div>
          </div>
        </Card>

        {/* Quick Prompts */}
        <QuickPrompts
          artifactType="opportunity"
          sectionType="assessment"
          onPromptSelect={handleQuickPrompt}
        />

        {/* Conversational Input */}
        <ConversationalInput
          question={currentCriteria.prompt}
          placeholder={currentCriteria.placeholder}
          value={currentCriteria.content}
          onChange={handleContentChange}
          artifactType="opportunity"
          sectionType="assessment"
          userContext={`Criteria: ${currentCriteria.title}. Weight: ${currentCriteria.weight}x. Completed: ${completedCriteria}`}
        />

        {/* Navigation */}
        <div className="flex justify-between items-center">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentCriteriaIndex === 0}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Previous
          </Button>

          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Sparkles className="h-4 w-4" />
            <span>Criteria {currentCriteriaIndex + 1} of {criteriaData.length}</span>
          </div>

          <Button
            onClick={handleNext}
            disabled={!canProceed}
            className="flex items-center gap-2"
          >
            {isLastCriteria ? 'Complete Audit' : 'Next Criteria'}
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>

        {!canProceed && (
          <div className="text-center">
            <p className="text-sm text-muted-foreground">
              Evaluate this criteria to continue your assessment
            </p>
          </div>
        )}
      </div>
    </div>
  );
};